import type React from "react";
import {Box, LinearProgress, Skeleton, Stack, Typography} from "@mui/material";
import {CustomCard} from "~/components/Card/Card";
import {SupplierCountryFlag} from "~/pages/supplier-360/components/SupplierCountryFlag";
import {useSupplierFirmographicsQuery} from "~/pages/supplier-360/hooks/useSupplierFirmographicsQuery";
import {SnapshotMeta} from "~/pages/supplier-360/components/pdl/SnapshotMeta";
import {formatPct} from "~/pages/supplier-360/components/pdl/pdlFormat";

const COUNTRY_CODE_RE = /^[A-Za-z]{2}$/;

type FootprintRow = {code: string, count: number, sharePct: number};

/** Top countries by headcount, from `employee_count_by_country` — raw counts turned into a share of the total client-side (the backend doesn't compute or return a percentage). */
function buildRows(byCountry: Record<string, number>): FootprintRow[] {
    const total = Object.values(byCountry).reduce((sum, count) => sum + count, 0);
    if (!total) return [];
    return Object.entries(byCountry)
        .map(([code, count]) => ({code, count, sharePct: (count / total) * 100}))
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);
}

/** Overview "Workforce footprint": the top countries by headcount. Hidden entirely once loaded with nothing to show. */
export const WorkforceFootprintCard: React.FC<{entityId: number}> = ({entityId}) => {
    const {data: firmographics, isLoading} = useSupplierFirmographicsQuery(entityId);
    const rows = buildRows(firmographics?.employee_count_by_country ?? {});

    if (!isLoading && rows.length === 0) return null;

    return (
        <CustomCard title="Workforce footprint" sx={{flex: 1, minWidth: 0}}>
            {isLoading && <Skeleton variant="rectangular" height={120}/>}
            {!isLoading && (
                <Stack gap={1}>
                    {rows.map(row => (
                        <Box key={row.code} sx={{display: 'grid', gridTemplateColumns: '20px minmax(0, 190px) 1fr 44px', alignItems: 'center', gap: 1.25}}>
                            <SupplierCountryFlag country={COUNTRY_CODE_RE.test(row.code)
                                ? {id: 0, name: row.code, code_a2: row.code.toUpperCase(), code_a3: '', flag: ''} : null}/>
                            <Typography variant="body2" noWrap>{row.code}</Typography>
                            <LinearProgress variant="determinate" value={Math.min(100, row.sharePct)}
                                            aria-label={`${row.code} share of headcount`}/>
                            <Typography variant="caption" sx={{textAlign: 'right'}}>{formatPct(row.sharePct)}</Typography>
                        </Box>
                    ))}
                    <SnapshotMeta retrievedAt={firmographics?.retrieved_at}/>
                </Stack>
            )}
        </CustomCard>
    );
};
